import type {
  LineageAgreement,
  LineageField,
  LineageFieldCluster,
} from "./lineage.types";

interface AgreementStyle {
  pill: string;
  cell: string;
  text: string;
  tip: string;
}

export const AGREEMENT_STYLE: Record<LineageAgreement, AgreementStyle> = {
  unanimous: {
    pill: "bg-[#DCFCE7] text-[#15803D]",
    cell: "bg-[#F0FDF4] text-[#14532D]",
    text: "text-[#15803D]",
    tip: "Every source that recorded this field recorded the same value.",
  },
  majority: {
    pill: "bg-[#FEF3C7] text-[#B45309]",
    cell: "bg-[#FFFBEB] text-[#78350F]",
    text: "text-[#B45309]",
    tip: "Sources disagree, but one value is carried by more than half of the records that populate this field.",
  },
  conflict: {
    pill: "bg-[#FEE2E2] text-[#B91C1C]",
    cell: "bg-[#FEF2F2] text-[#7F1D1D]",
    text: "text-[#B91C1C]",
    tip: "Sources disagree with no value carried by more than half of the records — a steward decision is what elected the canonical value.",
  },
  absent: {
    pill: "bg-[#F1F5F9] text-[#475569]",
    cell: "bg-white text-[#475569]",
    text: "text-[#475569]",
    tip: "No source recorded a value for this field.",
  },
};

export const AGREEMENT_LABEL: Record<LineageAgreement, string> = {
  unanimous: "All agree",
  majority: "Majority",
  conflict: "Conflict",
  absent: "Not recorded",
};

export type CellVerdict = "plurality" | "divergent" | "empty";

export type AgreementIndex = Record<
  LineageField,
  Record<string, CellVerdict> | undefined
>;

/**
 * member id → whether its value for a field is the plurality one, so the grid
 * can tint the record that diverges from what the other sources reported.
 */
export function buildAgreementIndex(
  clusters: LineageFieldCluster[],
): AgreementIndex {
  const index = {} as AgreementIndex;
  for (const cluster of clusters) {
    const byMember: Record<string, CellVerdict> = {};
    for (const value of cluster.values) {
      for (const memberId of value.member_ids) {
        byMember[memberId] = value.is_plurality ? "plurality" : "divergent";
      }
    }
    index[cluster.field] = byMember;
  }
  return index;
}

export function cellClassName(
  cluster: LineageFieldCluster,
  verdict: CellVerdict,
): string {
  if (verdict === "empty") return "bg-white text-[#94A3B8]";
  if (verdict === "plurality") {
    return cluster.agreement === "unanimous"
      ? AGREEMENT_STYLE.unanimous.cell
      : "bg-[#F0FDF4] text-[#14532D]";
  }
  return cluster.agreement === "conflict"
    ? AGREEMENT_STYLE.conflict.cell
    : AGREEMENT_STYLE.majority.cell;
}

export const REVIEW_TYPE_LABEL: Record<string, string> = {
  merge: "Merge candidate",
  split: "Over-cluster candidate",
};
